import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { AiResultDialog, useAiRunner } from "@/components/ai/AiResultDialog";
import { CommandActionDialog, ACTION_DEFS } from "@/components/CommandActions";
import { NAV, ADMIN_NAV } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ScrollText, GitCompareArrows, HelpCircle, Sparkles, History } from "lucide-react";

// Global ⌘K / Ctrl+K command palette: jump to any page or fire an AI action from anywhere.
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [recentPaths, setRecentPaths] = useState([]);
  const [lastAi, setLastAi] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const { can, ai, isAdmin, isSuperAdmin } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const aiRunner = useAiRunner();
  const aiOn = !!(ai?.available && ai?.canUse);
  const ALL_NAV = [...NAV, ...ADMIN_NAV];

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("open-command-palette", onOpen);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("open-command-palette", onOpen);
    };
  }, []);

  // Remember recently visited pages (nav destinations only).
  useEffect(() => {
    if (!ALL_NAV.some((n) => n.to === location.pathname)) return;
    try {
      const cur = JSON.parse(localStorage.getItem("recentNavPaths") || "[]");
      const next = [location.pathname, ...cur.filter((p) => p !== location.pathname)].slice(0, 6);
      localStorage.setItem("recentNavPaths", JSON.stringify(next));
    } catch { /* ignore */ }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load recents (and the last AI action) whenever the palette opens or an action is recorded.
  useEffect(() => {
    const load = () => {
      try { setRecentPaths(JSON.parse(localStorage.getItem("recentNavPaths") || "[]")); } catch { setRecentPaths([]); }
      try { setLastAi(JSON.parse(localStorage.getItem("lastAiAction") || "null")); } catch { setLastAi(null); }
    };
    if (open) load();
    window.addEventListener("ai-action-recorded", load);
    return () => window.removeEventListener("ai-action-recorded", load);
  }, [open]);

  const navVisible = (n) => {
    if (n.aiGated) return aiOn;
    if (n.adminOnly) return isAdmin || isSuperAdmin;
    return !n.perm || can(n.perm);
  };
  const goto = (to) => { setOpen(false); navigate(to); };
  const runAi = (title, path, body) => { setOpen(false); aiRunner.run(title, path, body); };
  const runAction = (key) => { setOpen(false); setActionKey(key); };
  // Guarded write actions the current admin may run (permission-gated) from the palette.
  const actionItems = Object.entries(ACTION_DEFS)
    .filter(([, def]) => def.perm.some((p) => can(p)))
    .map(([key, def]) => ({ key, ...def }));
  const navItems = ALL_NAV.filter(navVisible);
  const recentItems = recentPaths
    .map((p) => ALL_NAV.find((n) => n.to === p))
    .filter(Boolean)
    .filter(navVisible)
    .filter((n) => n.to !== location.pathname)
    .slice(0, 4);

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder={t("cmd.placeholder")} data-testid="command-palette-input" />
        <CommandList>
          <CommandEmpty>{t("cmd.empty")}</CommandEmpty>
          {(recentItems.length > 0 || (aiOn && lastAi)) && (
            <CommandGroup heading={t("cmd.recentGroup")}>
              {aiOn && lastAi && (
                <CommandItem value={`recent ai ${lastAi.title}`} onSelect={() => runAi(lastAi.title, lastAi.path, lastAi.body)} data-testid="cmd-recent-ai">
                  <Sparkles size={15} className="mr-2 text-primary" /> {lastAi.title}
                </CommandItem>
              )}
              {recentItems.map((n) => (
                <CommandItem key={`recent-${n.to}`} value={`recent ${t(n.key)} ${n.to}`} onSelect={() => goto(n.to)} data-testid={`cmd-recent-${n.testid}`}>
                  <History size={15} className="mr-2 text-zinc-400" /> {t(n.key)}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {aiOn && (
            <CommandGroup heading={t("cmd.aiGroup")}>
              <CommandItem value="ai summarize action log recent activity" onSelect={() => runAi(t("ai.summaryTitle"), "/ai/summarize-actions", { language: lang, limit: 60 })} data-testid="cmd-ai-summarize">
                <ScrollText size={15} className="mr-2 text-primary" /> {t("ai.summaryTitle")}
              </CommandItem>
              <CommandItem value="ai explain drift reconciliation report" onSelect={() => runAi(t("ai.driftTitle"), "/ai/explain-drift", { language: lang })} data-testid="cmd-ai-drift">
                <GitCompareArrows size={15} className="mr-2 text-primary" /> {t("ai.driftTitle")}
              </CommandItem>
              <CommandItem value="ai help assistant ask a question docs" onSelect={() => goto("/ai-assistant")} data-testid="cmd-ai-help">
                <HelpCircle size={15} className="mr-2 text-primary" /> {t("ai.helpTitle")}
              </CommandItem>
            </CommandGroup>
          )}
          {actionItems.length > 0 && (
            <CommandGroup heading={t("cmdact.group")}>
              {actionItems.map((a) => (
                <CommandItem key={a.key} value={`action ${t(a.titleKey)} ${a.key}`} onSelect={() => runAction(a.key)} data-testid={`cmd-action-${a.key}`}>
                  <a.icon size={15} className={`mr-2 ${a.destructive ? "text-red-500" : "text-primary"}`} /> {t(a.titleKey)}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandGroup heading={t("cmd.navGroup")}>
            {navItems.map((n) => (
              <CommandItem key={n.to} value={`${t(n.key)} ${n.to}`} onSelect={() => goto(n.to)} data-testid={`cmd-nav-${n.testid}`}>
                <n.icon size={15} className="mr-2 text-zinc-500" /> {t(n.key)}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
      <AiResultDialog open={aiRunner.state.open} onOpenChange={(v) => !v && aiRunner.close()} title={aiRunner.state.title} loading={aiRunner.state.loading} error={aiRunner.state.error} content={aiRunner.state.content} sources={aiRunner.state.sources} empty={aiRunner.state.empty} />
      <CommandActionDialog actionKey={actionKey} onClose={() => setActionKey(null)} />
    </>
  );
}
